import { change } from "redux-form";
import FormConstants from "./DataEntryFieldsConstants";

/**
 * Writes a single field's value into a redux-form instance via redux-form's
 * own `change()` action creator (mirrors the `resetFieldValue` pattern already
 * used in DataEntryDetailsRightPane.tsx - NOT the raw `@@redux-form/CHANGE`
 * dispatch hack used by that file's local `changeFieldValue` helper).
 *
 * Shared by both the classic RightPane Details tab and the Quick Entry
 * field-group row so both views read/write the same DATA_ENTRY_FIELDS_FORM
 * state (Spec #34098 / F1a, AC11, Business Rule 14).
 *
 * Never logs `value` - it may be PHI-adjacent form data (see
 * .claude/rules/hipaa-compliance.md).
 */
export function writeFieldToForm(
    dispatch: Function,
    fieldName: string,
    value: any,
    formName: string = FormConstants.DATA_ENTRY_FIELDS_FORM,
): void {
    dispatch(change(formName, fieldName, value));
}

export default writeFieldToForm;

/**
 * Real redux-form validation state for one field, threaded down from
 * DataEntryDetailsRightPane.tsx (via redux-form's own getFormMeta/
 * getFormSyncErrors selectors) into the shared field components that used to
 * be wrapped directly in a real <Field> - RequestTypePurposeField,
 * AuthorizationDateField, RequestDateField, ClinicResponsibleField,
 * CaseNumberField (Spec #34098 / F1a extraction; Spec #34102 / F1d
 * regression fix - see each component's own doc comment).
 *
 * Deliberately lighter than redux-form's own WrappedFieldMetaProps - this is
 * the minimum shape these 5 components need to reproduce their pre-
 * extraction error-display behavior, not a general redux-form meta
 * passthrough.
 */
export interface ClassicFieldMeta {
    readonly touched: boolean;
    readonly active?: boolean;
    readonly error?: string;
}

/**
 * Meta stub for Quick Entry's own rendering path, which never passes a real
 * `meta` prop (it has its own FieldGroupRowStatus-driven red/yellow/green
 * visual gating instead of redux-form's touched/error - Spec #34102 / F1d).
 * `valid: true` here matches this codebase's pre-existing STATIC_META
 * convention exactly (never show an error) - not a placeholder to fill in
 * later.
 */
export const UNVALIDATED_FIELD_META = { touched: false, active: false, valid: true as const, error: undefined };

/**
 * Restores the exact touched+error display gate ReduxFormDateField/
 * ReduxFormTextField's own `meta.touched && !meta.active && !meta.valid`
 * check used before the F1a extraction removed the real <Field> wrapper
 * (Spec #34102 / F1d regression fix) - for the DateField/TextField-based
 * components (AuthorizationDateField, RequestDateField, CaseNumberField),
 * which take a plain `errMsg` string rather than a `meta` object.
 */
export function classicFieldErrorMessage(meta?: ClassicFieldMeta): string {
    if (!meta || !meta.touched || meta.active) {
        return "";
    }
    return meta.error ?? "";
}

/**
 * Same gate as classicFieldErrorMessage, but shaped for DropdownField's own
 * `meta` prop contract (DropdownField requires `valid`, not just `error` -
 * it computes its own errMsg internally from `touched && !active && !valid`).
 * Used by RequestTypePurposeField/ClinicResponsibleField, which mount
 * DropdownField directly.
 */
export function toDropdownFieldMeta(meta?: ClassicFieldMeta) {
    if (!meta) {
        return UNVALIDATED_FIELD_META;
    }
    return { touched: meta.touched, active: Boolean(meta.active), valid: !meta.error, error: meta.error };
}

/**
 * Formats a date field's form value for Quick Entry's read-only row display
 * (QuickEntryFieldGroupList's `row.value` span).
 *
 * Root cause (Spec #34102 / F1d bug, live-testing report): mapAuthorization/
 * RequestDateToFieldGroupRow used to build row.value via `String(formValue)`.
 * DATA_ENTRY_FIELDS_FORM's AUTHORIZATION_DATE/REQUEST_DATE values are real
 * `Date` objects once a specialist picks one (DateField.tsx's onChangeDate
 * calls onChange(date) with the picker's Date, not a string) - `String()` on
 * a Date calls Date.prototype.toString(), rendering e.g. "Thu Jul 16 2026
 * 11:06:00 GMT-0400 (Eastern Daylight Time)" verbatim instead of a formatted
 * date.
 *
 * Mirrors the "July 16, 2026" convention RecordsRequestedField.tsx's own
 * (unexported) formatServiceDate already established for Quick Entry's
 * Service Date-From/To rows - same `toLocaleDateString("en-US", { year:
 * "numeric", month: "long", day: "numeric" })` call - so every Quick Entry
 * row formats dates the same way. Tolerates a raw ISO string too (values
 * restored from redux-form's initial state, or a not-yet-Date API value)
 * the same way RecordsRequestedField.tsx's own coerceDate does. Falls back
 * to the raw value for a genuinely unparseable value (e.g. the INVALID_DATE
 * sentinel) rather than surfacing "Invalid Date" from toLocaleDateString.
 */
export function formatFieldGroupDate(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * MM/DD/YYYY for a Date or date string (yyyy-MM-dd / ISO). Date-only strings are parsed by
 * PARTS, not by `new Date(...)`, to avoid the UTC-midnight timezone day-shift that would show
 * the previous day west of UTC.
 *
 * This is the short form the Quick Entry DATE rows display (Authorization Date, Request Date,
 * and - added with AB#35137 / AB#35144 - Received Date and Due Date), as distinct from
 * formatFieldGroupDate's long "July 28, 2026" form used by other row types. Shared here so the
 * new rows don't add a fourth private copy; AuthorizationDateField's formatAuthDateValue and
 * RequestDateField's formatReqDateValue are pre-existing duplicates of this logic and are left
 * alone deliberately (structural cleanup belongs in its own commit - .claude/rules/tidy-first.md).
 *
 * AB#37982: the Date branch used to read `value.toISOString()` unconditionally, which is only
 * correct for an AI-extracted, date-only value (a Date at UTC midnight - see isUtcMidnightDate).
 * A Date the specialist just picked or typed is LOCAL midnight, and in any timezone AHEAD of UTC,
 * local midnight's UTC calendar day is the day BEFORE - so a freshly picked/typed date showed one
 * day early in the row summary. Branches on isUtcMidnightDate so each case reads its own correct
 * calendar day, same rule persistQuickEntrySelectionsToTransaction's addDate now uses on the save
 * path (DataEntryDetails.tsx) - see isUtcMidnightDate's own docblock for the shared invariant.
 */
export function formatShortFieldGroupDate(value: Date | string): string {
    let isoDate: string | undefined;
    if (typeof value === "string") {
        isoDate = value;
    } else if (value instanceof Date && !isNaN(value.getTime())) {
        isoDate = isUtcMidnightDate(value)
            ? value.toISOString().substring(0, 10)
            : `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
    }
    const parts = isoDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : String(value);
}

/**
 * True when `value` sits at EXACTLY UTC midnight - the shape models/intake.ts's tryParseDate
 * produces for an AI-extracted, date-only value ("yyyy-MM-dd" parses as UTC midnight per the
 * ECMA-262 Date Time String Format). DateField's picker (@date-io/date-fns) never constructs a
 * UTC-midnight Date for a value the specialist picks or types - only local midnight - so this is
 * an unambiguous way to tell "AI-seeded, unedited" apart from "specialist-edited" without a
 * separate flag.
 *
 * This is the read-side half of the floating-calendar-day invariant toDisplayDate documents (see
 * its own docblock): a Date at UTC midnight denotes THAT UTC calendar day; any other Date denotes
 * its LOCAL calendar day. formatShortFieldGroupDate (this file) and
 * DataEntryDetails.tsx's persistQuickEntrySelectionsToTransaction addDate helper (AB#37982) both
 * need to branch on exactly this condition, so it lives here once rather than as two ad-hoc
 * inline checks.
 *
 * The two-case invariant this function assumes depends on the AI-extracted value NEVER carrying a
 * time-of-day - verified against the API contract, not just the client-side parser: Authorization/
 * Request/Received/DueDate are `DateOnly?` on the analysis response (RequestAnalysisData.cs), and
 * both of that type's `[JsonConverter(typeof(DateConverter))]` implementations
 * (Verisma.ROIS.API.Managers/Interfaces/Intake/DateConverter.cs,
 * Verisma.ROIS.API/DateConverter.cs) unconditionally `WriteValue(value.ToString("yyyy-MM-dd"))` -
 * no time, no offset, ever. So `tryParseDate`'s `new Date(date)` on one of these four fields is
 * always exactly UTC midnight; this function's "any other Date is a specialist edit" branch is
 * never reached by an un-edited AI value from these fields. This does NOT extend to
 * createdAtUtc/lastAttemptedAtUtc/completedAtUtc, which ARE `DateTime` and DO carry a time-of-day -
 * but those three are read-only analysis metadata, never routed through formatShortFieldGroupDate
 * or isUtcMidnightDate. See QuickEntryDateRoundTripTimezone.test.ts's "backend contract" describe
 * block for the pinning test.
 */
export function isUtcMidnightDate(value: Date): boolean {
    return (
        value.getUTCHours() === 0 &&
        value.getUTCMinutes() === 0 &&
        value.getUTCSeconds() === 0 &&
        value.getUTCMilliseconds() === 0
    );
}

/**
 * MM/DD/YYYY for a Date already known to be at UTC midnight (see isUtcMidnightDate) - i.e. an
 * AI-extracted, date-only value. Reads the value's OWN UTC calendar day, matching
 * formatShortFieldGroupDate's UTC branch above. Callers must guard with isUtcMidnightDate first;
 * this assumes it rather than re-checking, so it stays a single-purpose formatter.
 *
 * Used by DataEntryDetails.tsx's addDate helper (AB#37982) so an un-edited AI value is saved on
 * its own UTC calendar day instead of getMomentDate's local rendering, which shifts a UTC-midnight
 * instant back one day in any timezone BEHIND UTC.
 */
export function utcMidnightDateToMMDDYYYY(value: Date): string {
    const month = String(value.getUTCMonth() + 1).padStart(2, "0");
    const day = String(value.getUTCDate()).padStart(2, "0");
    return `${month}/${day}/${value.getUTCFullYear()}`;
}

/**
 * AB#38463: the SHORT ("8/16/2026") locale rendering of a Service Date, on the correct calendar
 * day for either shape the value can take - the same isUtcMidnightDate branch
 * formatShortFieldGroupDate applies to Due/Received, and that RecordsRequestedField's
 * formatServiceDate applies to the long ("August 16, 2026") form.
 *
 * Lives here, beside isUtcMidnightDate, rather than inline at its one call site
 * (DataEntryDetails.tsx's getQuickEntryRecordsSummary) because an inline copy is exactly how the
 * unconditional-UTC read survived in two places long enough to be reported: the rule belongs in
 * one file so a future change cannot fix one surface and miss the other.
 */
export function formatQuickEntryServiceDateShort(value: Date): string {
    return value.toLocaleDateString(undefined, { timeZone: isUtcMidnightDate(value) ? "UTC" : undefined });
}

/**
 * AB#38463: strips a picker value down to LOCAL midnight on the day it shows.
 *
 * DateField's picker (@date-io/date-fns) sets year/month/day in local time but CARRIES THE
 * PREVIOUS VALUE'S TIME-OF-DAY along. An AI-seeded UTC-midnight value read on a US Eastern host
 * has a local time-of-day of exactly 20:00, so re-picking a day yields <day> 20:00 - and in EDT
 * (UTC-4) that instant IS the next day's UTC midnight. Picking 31 Mar 2023 then displayed and
 * saved as 1 Apr 2023 (reported).
 *
 * That collision is why this exists as an INPUT normalizer instead of another read-side branch:
 * "31 Mar 20:00 EDT" and "1 Apr 00:00 UTC" are the same instant, so isUtcMidnightDate - the flag
 * every read and the save path use to tell an AI value from a picked one - is true for both and
 * cannot separate them. No formatter can. The ambiguity has to be removed where the value is
 * created. Local midnight is never UTC midnight in any zone with a non-zero offset, and in UTC
 * itself both reads agree anyway.
 *
 * SCOPE - the guarantee is per-call-site, NOT global (AB#38463, Nelson Omuto, PR !10139 review).
 * isUtcMidnightDate is only unambiguous downstream of a call site that applies this. Today that
 * means the two Quick Entry Service Date pickers in DataEntryDetails.tsx's
 * renderQuickEntryRowContent, and nothing else. In particular InfoToRelease.tsx's changeFromDate /
 * changeToDate - Classic's own Service Date pickers, which reach the SAME
 * updateInformationToReleaseApi and the SAME displayedServiceDay - pass the picker value through
 * un-normalized, so a Classic re-pick that inherits a 20:00 EDT time-of-day still persists the
 * next day. That is a known, deliberate carve-out: this work is scoped to Quick Entry. Do not read
 * this helper as making the flag safe everywhere, and do not "simplify" a downstream read on the
 * assumption that it is.
 *
 * Call this ONLY where the value is known to come from a specialist's picker. Do NOT apply it in
 * a handler that also accepts AI-extracted values: local-midnight-ing a UTC-midnight instant
 * moves it a calendar day BACK everywhere behind UTC, which is the opposite bug.
 */
export function toLocalCalendarDayMidnight(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
}

/**
 * AB#38463: "yyyy-MM-dd" for the calendar day a Date DENOTES - the write-side twin of
 * formatShortFieldGroupDate, for endpoints that take a date-only string.
 *
 * `toISOString().substring(0, 10)` looks like the obvious way to do this and is wrong for half
 * the values: it reads the UTC day unconditionally, which is right only for an AI-extracted
 * UTC-midnight value. A specialist-picked date denotes its LOCAL day, and in any timezone ahead
 * of UTC its UTC day is the day BEFORE - so that shortcut silently persisted the wrong day. Same
 * floating-calendar-day invariant isUtcMidnightDate documents, applied on save.
 */
export function floatingCalendarDayIso(value: Date): string {
    if (isUtcMidnightDate(value)) {
        return value.toISOString().substring(0, 10);
    }
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${value.getFullYear()}-${month}-${day}`;
}

/**
 * AB#37843: normalizes a "floating" calendar-day Date for DISPLAY in a DateField picker.
 *
 * An AI-extracted, date-only value (models/intake.ts's tryParseDate does `new Date(date)` on a
 * plain "yyyy-MM-dd" string) parses at UTC MIDNIGHT - per the ECMA-262 Date Time String Format,
 * a date-only ISO string has no time component, so it's UTC; a full datetime string with no
 * explicit offset would instead be local. DateField's picker (@date-io/date-fns) formats Date
 * objects using LOCAL getters - correct for a real instant, but wrong for a floating calendar-day
 * value like this one: in any timezone behind UTC (every US timezone), UTC midnight IS the
 * previous calendar day locally. On a fresh transaction load, with zero edits, that showed the
 * picker one calendar day earlier than the row's own UTC-safe summary
 * (formatShortFieldGroupDate above) for the exact same Date - "08/10/2024" in the picker vs
 * "08/11/2024" in the collapsed summary.
 *
 * Rebuilds the SAME calendar day at LOCAL midnight for a UTC-midnight value, so the picker's
 * local-time read lands on the correct day. Any OTHER Date is returned untouched. Strings are
 * passed through untouched too: the picker parses them by parts, so they never take the
 * local-getter path this corrects for.
 *
 * AB#37982 (round-1 review, W1): the rebuild used to run UNCONDITIONALLY, and this docblock claimed
 * it was "a no-op for a value that's already local midnight ... local midnight's UTC Y/M/D never
 * differs from its own local Y/M/D when re-derived from the same instant". That claim is FALSE for
 * every timezone AHEAD of UTC, where local midnight IS the previous UTC day - so the rebuild
 * produced the day BEFORE, and a specialist who picked or typed 01/03/2026 saw the field redisplay
 * 01/02/2026 (observed in a real browser on Dev-Test under Asia/Kolkata while building
 * e2e/tests/qe-date-timezone-summary.authed.spec.ts). What is wrong is the FIELD's own text: none
 * of the four components passes an `onBlur`, so DateField's own onBlur does re-parse the DISPLAYED
 * text into `mirrorData` - but a local Classic capture (Asia/Kolkata, gate removed) showed the left
 * transaction-summary panel still reading the correct day for that same value, because the autosave
 * round-trip refreshes `leftPaneTransactionDetails` from the persisted transaction. Do not restate
 * the panel as wrong without re-observing it.
 *
 * Now gated on isUtcMidnightDate - the same read-side branch formatShortFieldGroupDate and
 * persistQuickEntrySelectionsToTransaction's addDate use, so all three surfaces agree on which
 * calendar day a value denotes. Behind UTC (every US timezone) the gate changes nothing: local
 * midnight's UTC calendar day IS the same day there, so the old rebuild returned an equal instant.
 * Regression cover: QuickEntryDatePickerAheadOfUtc.test.tsx (ahead of UTC, all four fields) plus the
 * pre-existing AuthorizationDateFieldTimezone/SiblingDateFieldsTimezone suites (behind UTC).
 *
 * DISPLAY ONLY - pass this to a DateField's `value`, never to the onChange/write path. The stored
 * value must stay exactly what the API/extraction supplied.
 *
 * Applied by the four AI-seeded date field components (Authorization / Request / Received / Due),
 * which each hand a `values[FormConstants.*]` Date straight to the same DateField.
 *
 * Classic is NOT affected, and deliberately needs no equivalent (AB#37843). An earlier draft of
 * this docblock claimed Classic's Received/Due rows still showed the previous day, on the grounds
 * that Classic renders those two through `<Field component={ReduxFormDateField}>`
 * (DataEntryDetailsRightPane.tsx ~2167 / ~2182) rather than the shared components, and that
 * ReduxFormDateField passes `input.value` through unnormalized. Both of those facts are true, but
 * the conclusion was wrong: Classic does not display AI-seeded values. Intake is blocked in
 * Classic - it reads these fields from the API-persisted transaction, and
 * `persistedOrDraft(transactionDetails.date?.received, formValues[...])` returns the persisted
 * value whenever there is one, falling back to the form draft only for a field the transaction has
 * never carried (see persistedOrDraft.ts: "Classic Mode is unaffected wherever a value IS
 * persisted"). So the UTC-midnight Dates this helper exists to correct never reach Classic's
 * pickers. Do not "fix" ReduxFormDateField on the strength of the code path alone.
 */
// AB#38460: a Date in means a Date out - the function returns non-Date input unchanged, so this is
// the actual contract, not a convenience. Stated as an overload so callers whose prop types are
// Date-only (a picker's minDate / maxDate) do not need a cast to use it.
export function toDisplayDate(value: Date): Date;
export function toDisplayDate(value: Date | undefined): Date | undefined;
// Declaring overloads hides the implementation signature, so the general case needs one too -
// the four wrapper components pass a redux-form value, which is Date | string | undefined.
export function toDisplayDate(value?: Date | string): Date | string | undefined;
export function toDisplayDate(value?: Date | string): Date | string | undefined {
    if (!(value instanceof Date) || isNaN(value.getTime())) {
        return value;
    }
    if (!isUtcMidnightDate(value)) {
        // A specialist-picked/typed value: already local midnight, so the picker's local read is
        // already on the right day. Rebuilding it from UTC parts is what showed the day BEFORE
        // ahead of UTC (AB#37982 W1).
        return value;
    }
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

/**
 * Value-based equality for `Date | string` field-group dates - used to decide whether a row is
 * still showing the AI's extracted value (the "AI pre-selected" chip) rather than a specialist
 * edit. Returns false when either side is absent, so an unset field is never "equal" to an
 * extraction.
 */
export function sameFieldGroupDate(a?: Date | string, b?: Date | string): boolean {
    if (!a || !b) {
        return false;
    }
    const ta = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const tb = b instanceof Date ? b.getTime() : new Date(b).getTime();
    return !isNaN(ta) && ta === tb;
}
